import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../api.js';
import { ChatComposer } from '../components/chat/ChatComposer.js';
import { ChatMessage } from '../components/chat/ChatMessage.js';
import { QuotaCounter } from '../components/chat/QuotaCounter.js';
import { usePremiumDialog } from '../context/PremiumDialogContext.js';
import { useChatStream } from '../hooks/useChatStream.js';
import { useTrack } from '../hooks/useTrack.js';

function EmptyState() {
  const { t } = useTranslation('coach');
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
      <svg
        aria-hidden="true"
        className="h-10 w-10 text-ink-soft dark:text-ink-soft"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.625 9.75a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0
             .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375m-13.5
             3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14
             0 01.778-.332 48.294 48.294 0 005.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394
             48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"
        />
      </svg>
      <div>
        <p className="text-sm font-medium text-ink dark:text-ink">{t('empty.heading')}</p>
        <p className="mt-1 text-sm text-ink-soft dark:text-ink-soft">{t('empty.body')}</p>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const { t } = useTranslation('coach');
  const { threadId: urlThreadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>(urlThreadId);
  const {
    messages,
    quota,
    battery,
    isStreaming,
    send,
    confirmAction,
    checkIn,
    loadThread,
    clearMessages,
  } = useChatStream();
  const [checkingIn, setCheckingIn] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [hydrating, setHydrating] = useState(Boolean(urlThreadId));
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const track = useTrack();
  const { openDialog: openPremiumDialog } = usePremiumDialog();
  const bottomRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const skipHydrateThreadRef = useRef<string | null>(null);
  const batteryEmpty = battery ? battery.regenCredits + battery.earnedCredits <= 0 : false;

  // Auto-scroll to bottom when new content arrives
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages is the intended trigger; bottomRef is stable
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(
    async (text: string) => {
      let tid = currentThreadId;
      if (!tid) {
        tid = crypto.randomUUID();
        setCurrentThreadId(tid);
        skipHydrateThreadRef.current = tid;
        navigate(`/chat/${tid}`, { replace: true });
      }
      await send(text, tid);
    },
    [currentThreadId, send, navigate],
  );

  useEffect(() => {
    setCurrentThreadId(urlThreadId);
    setHydrateError(null);

    if (!urlThreadId) {
      clearMessages();
      setHydrating(false);
      return;
    }

    if (skipHydrateThreadRef.current === urlThreadId) {
      skipHydrateThreadRef.current = null;
      setHydrating(false);
      return;
    }

    let cancelled = false;
    setHydrating(true);
    loadThread(urlThreadId)
      .catch(() => {
        if (!cancelled) setHydrateError(t('history.loadError'));
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [urlThreadId, loadThread, clearMessages, t]);

  useEffect(() => {
    if (batteryEmpty) void track('upsell_shown_from_battery');
  }, [batteryEmpty, track]);

  const handleCheckIn = useCallback(async () => {
    setCheckingIn(true);
    try {
      await checkIn();
    } finally {
      setCheckingIn(false);
    }
  }, [checkIn]);

  const handleUpgrade = useCallback(() => {
    void track('upgrade_from_battery');
    openPremiumDialog();
  }, [openPremiumDialog, track]);

  const handleShare = useCallback(async () => {
    setSharing(true);
    try {
      const { data, response } = await api.GET('/me/referral', {});
      if (!response.ok || !data) throw new Error(`HTTP ${response.status}`);
      if (navigator.share) {
        await navigator.share({ text: data.shareText });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(data.shareText);
      }
    } finally {
      setSharing(false);
    }
  }, []);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Title row + quota counter */}
      <div className="flex items-center justify-between py-2 shrink-0">
        <h2 className="text-xl font-medium tracking-tight">{t('title')}</h2>
        <div className="flex items-center gap-3">
          <Link to="/chat" className="text-sm font-medium text-ink-soft hover:text-ink">
            {t('history.title')}
          </Link>
          <QuotaCounter
            quota={quota}
            battery={batteryEmpty ? null : battery}
            onUpgrade={handleUpgrade}
          />
        </div>
      </div>

      {/* Message thread (scrollable) */}
      <div
        ref={threadRef}
        className="flex-1 min-h-0 overflow-y-auto"
        role="log"
        aria-label="Conversation"
        aria-live="polite"
      >
        {hydrating ? (
          <div className="flex flex-1 items-center justify-center py-12 text-sm text-ink-soft">
            {t('history.loading')}
          </div>
        ) : hydrateError ? (
          <div className="flex flex-1 items-center justify-center py-12 text-sm text-clay">
            {hydrateError}
          </div>
        ) : messages.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="flex flex-col gap-4 py-4">
            {messages.map((msg) => (
              <ChatMessage
                key={msg.localId}
                message={msg}
                onConfirmAction={confirmAction}
                onSelectClarification={(id, selected) => confirmAction(id, 'select', selected)}
              />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Composer — sticks to the bottom of the flex column */}
      <div className="shrink-0 border-t border-husk pt-4 pb-2 dark:border-husk">
        {batteryEmpty && (
          <div className="mb-3">
            <QuotaCounter
              quota={null}
              battery={battery}
              onCheckIn={handleCheckIn}
              checkingIn={checkingIn}
              onShare={handleShare}
              sharing={sharing}
              onUpgrade={handleUpgrade}
            />
          </div>
        )}
        <ChatComposer onSend={handleSend} disabled={isStreaming || hydrating} />
        <p className="mt-2 text-center text-xs text-ink-soft dark:text-ink-soft">
          <kbd className="font-sans">⌘</kbd>
          <span> + </span>
          <kbd className="font-sans">↵</kbd>
          {` ${t('keyboardHint')}`}
        </p>
      </div>
    </div>
  );
}
